import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/*
 * Cazurile la furnizor (aplicatiile de business, pasul 2).
 *
 *   GET  /easeedesk/tickets/{id}/vendor-cases        cazurile tichetului
 *   POST /business-apps/cases/{caseId}/status        status, restore_ticket
 *   POST /business-apps/cases/{caseId}/reference     vendor_ref
 *   POST /business-apps/cases/{caseId}/replied       "Vendor replied"
 *   GET  /business-apps/{id}/vendor-cases?all=1      lista aplicatiei
 *   GET  /business-apps/{id}/case-settings           sarbatori, tiparul numarului, zile libere
 *   POST /business-apps/{id}/case-settings
 *
 * Nivelul urmator pleaca pe escaladarea din tichet, cu `case_id`.
 */

export type CaseStatusValue = 1 | 2 | 3 | 4 | 5 | 6

export const CASE_STATUS = { OPEN: 1, ACKNOWLEDGED: 2, WAITING_VENDOR: 3, WAITING_US: 4, RESOLVED: 5, CLOSED: 6 } as const

export type VendorCaseNextLevel = { contactId: number; name: string; email: string; level: number; levelNote: string }

export type VendorCaseHistory = { at: string; kind: string; text: string; by: string }

export type VendorCase = {
  id: number
  appId: number
  appName: string
  supportName: string
  ticketId: number
  ticketSubject: string
  status: number
  statusLabel: string
  vendorRef: string
  level: number
  dueAt: string | null
  dueBasis: string
  /** Asteapta primul raspuns la nivelul curent. */
  awaitingResponse: boolean
  overdue: boolean
  respondedAt: string | null
  firstResponseAt: string | null
  sentAt: string
  createdAt: string
  closedAt: string | null
  ticketWaiting: boolean
  /* numai pe tichet */
  transitions?: { value: number; label: string }[]
  nextLevel?: VendorCaseNextLevel | null
  history?: VendorCaseHistory[]
  escalationIds?: number[]
}

export type Option = { value: number; name: string }

export type TicketVendorCases = {
  ready: boolean
  canEdit?: boolean
  canWaitThirdParty?: boolean
  externalProviders?: Option[]
  waitingReasons?: Option[]
  cases: VendorCase[]
}

export function ticketVendorCasesQuery(ticketId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'vendor-cases', 'ticket', ticketId],
    queryFn: () => apiGet<TicketVendorCases>(`/easeedesk/tickets/${ticketId}/vendor-cases`),
    enabled: ticketId > 0,
  })
}

export type CaseWritten = { status: 'OK' | 'ERROR'; message: string; restored?: boolean }

export function setVendorCaseStatus(caseId: number, status: number, restoreTicket: boolean) {
  return apiPost<CaseWritten>(`/business-apps/cases/${caseId}/status`, { status, restore_ticket: restoreTicket ? '1' : '0' })
}

export function setVendorCaseReference(caseId: number, vendorRef: string) {
  return apiPost<CaseWritten>(`/business-apps/cases/${caseId}/reference`, { vendor_ref: vendorRef })
}

export function markVendorReplied(caseId: number) {
  return apiPost<CaseWritten>(`/business-apps/cases/${caseId}/replied`, {})
}

export function appVendorCasesQuery(appId: number, all: boolean) {
  return queryOptions({
    queryKey: ['business-apps', 'vendor-cases', 'app', appId, all],
    queryFn: () => apiGet<{ ready: boolean; cases: VendorCase[] }>(`/business-apps/${appId}/vendor-cases${all ? '?all=1' : ''}`),
    enabled: appId > 0,
  })
}

export type CaseSettings = {
  ready: boolean
  canEdit?: boolean
  holidayCountry?: string
  caseRefPattern?: string
  countries?: { value: string; label: string }[]
  upcomingHolidays?: { day: string; name: string }[]
  daysOff?: { day: string; note: string }[]
}

export function caseSettingsQuery(appId: number) {
  return queryOptions({
    queryKey: ['business-apps', 'case-settings', appId],
    queryFn: () => apiGet<CaseSettings>(`/business-apps/${appId}/case-settings`),
    enabled: appId > 0,
  })
}

export function saveCaseSettings(
  appId: number,
  fields: { holiday_country: string; case_ref_pattern: string; days_off: { day: string; note: string }[] },
) {
  return apiPost<CaseWritten>(`/business-apps/${appId}/case-settings`, fields as unknown as Record<string, PostValue>)
}

/** Clasa etichetei de stare: rosu peste termen, verde rezolvat, gri inchis. */
export function caseTone(c: Pick<VendorCase, 'status' | 'overdue'>): string {
  if (c.overdue) return 'label-danger'
  if (c.status === CASE_STATUS.CLOSED) return 'label-default'
  if (c.status === CASE_STATUS.RESOLVED) return 'label-success'
  if (c.status === CASE_STATUS.WAITING_US) return 'label-warning'
  if (c.status === CASE_STATUS.OPEN) return 'label-primary'
  return 'label-info'
}
